import { useEffect, useState } from "react";
import { Input, InputNumber, Radio, Select } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getMonitoringSettings, updateMonitoringSettings } from "../../api/monitoring";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";

const Row = ({ label, help, children }) => (
  <div className="grid grid-cols-3 gap-6 py-4 border-b border-[#F2F2F2] last:border-0">
    <div>
      <div className="font-bold text-[14px]">{label}</div>
      {help && <div className="text-[12px] text-[#00000080] mt-1">{help}</div>}
    </div>
    <div className="col-span-2">{children}</div>
  </div>
);

const MonitoringSettings = () => {
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const canConfigure = getModulePermission("monitoring", "canConfigure");
  const { data } = useQuery("monitoring-settings", getMonitoringSettings);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (data && !form) setForm(data);
  }, [data, form]);

  const save = useMutation(updateMonitoringSettings, {
    onSuccess: (d) => {
      showSuccessToast("Settings saved");
      setForm(d);
      queryClient.invalidateQueries("monitoring-settings");
    },
    onError: (e) => showErrorToast(e.message),
  });

  if (!form) return <div className="text-[14px] text-[#00000080]">Loading…</div>;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  return (
    <div className="bg-white rounded-lg p-5">
      <Row label="Held payments" help="What happens to a held withdrawal or crypto send if nobody reviews it in time.">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[14px]">
            After
            <InputNumber min={5} max={10080} value={form.holdTimeoutMinutes} onChange={set("holdTimeoutMinutes")} disabled={!canConfigure} />
            minutes without a decision:
          </div>
          <Radio.Group value={form.onHoldTimeout} onChange={set("onHoldTimeout")} disabled={!canConfigure}>
            <div className="flex flex-col gap-2">
              <Radio value="release">Release automatically (send the payment)</Radio>
              <Radio value="reject">Reject automatically (refund the customer)</Radio>
              <Radio value="keep">Keep holding until someone decides</Radio>
            </div>
          </Radio.Group>
          <div className="text-[12px] text-[#00000080]">
            Applies to holds already waiting too. Weekends and nights count, so set this to cover the longest gap with no reviewer.
          </div>
        </div>
      </Row>
      <Row label="Review deadline" help="Alerts not reviewed within this time are shown as overdue.">
        <div className="flex items-center gap-2 text-[14px]">
          <InputNumber min={1} max={720} value={form.reviewSlaHours} onChange={set("reviewSlaHours")} disabled={!canConfigure} /> hours
        </div>
      </Row>
      <Row label="Email alerts" help="Who is emailed when new alerts are raised, and from which severity.">
        <div className="flex gap-3">
          <Input value={form.notifyEmail} onChange={set("notifyEmail")} disabled={!canConfigure} className="max-w-[320px]" />
          <Select
            value={form.notifyMinSeverity}
            onChange={set("notifyMinSeverity")}
            disabled={!canConfigure}
            className="w-[200px]"
            options={[
              { value: "high", label: "High only" },
              { value: "medium", label: "Medium and high" },
              { value: "low", label: "All alerts" },
              { value: "off", label: "Don't email" },
            ]}
          />
        </div>
      </Row>
      {canConfigure && (
        <div className="flex justify-end pt-4">
          <button
            type="button"
            disabled={save.isLoading}
            className="bg-dark text-white rounded-[5px] px-5 py-2 font-bold text-[14px] disabled:opacity-50"
            onClick={() =>
              save.mutate({
                holdTimeoutMinutes: form.holdTimeoutMinutes,
                onHoldTimeout: form.onHoldTimeout,
                reviewSlaHours: form.reviewSlaHours,
                notifyEmail: form.notifyEmail,
                notifyMinSeverity: form.notifyMinSeverity,
              })
            }
          >
            {save.isLoading ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}
    </div>
  );
};

export default MonitoringSettings;
